import { Injectable, OnDestroy } from "@angular/core";
import { AbstractControl, AsyncValidator, ValidationErrors } from "@angular/forms";
import { ContextType } from "@charlesschwab/as-ui-core";
import { Store } from "@ngrx/store";
import { WireAbaCodes } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { Observable, Subject, combineLatest, of } from "rxjs";
import { catchError, map, takeUntil } from "rxjs/operators";
import { selectMasterAccount, selectPrimaryMaster, selectSubAccountId } from '../../../account-info-header/reducers';
import { selectIsCreateStandingAuth, selectIsCreateTransaction } from "../../../shared/reducers";
import { ValidateWireFlagsService } from "../../../shared/services/validate-WireFlags.service";
import { isPalAccountFlow } from "../../../shared/utilities/AccountFlow";
import { StandingAuthWireActions } from "../../../standing-authorizations/actions";
import { WireActions } from '../../actions';
import { ServiceError } from "../../models/shared/ServiceError";
import { WireDetailsApiResponse } from "../../models/wire/WireDetailsApiResponse";
import { WireDetailsRequest } from "../../models/wire/WireDetailsRequest";
import { DetailsResponse } from "../../models/wire/WireInfo";
import { WireService } from "../../services/wire.service";
import { selectUseStandingAuth, selectWireInfo, selectWireIntermediarySelection } from "../../reducers";
import { selectStandingAuthWireInfo, selectStandingAuthWireIntermediarySelection } from "../../../standing-authorizations/reducers";
import { IntegrationService } from "../../../shared/services/integration.service";

@Injectable({ providedIn: 'root' })
export class AbaAsyncValidator implements AsyncValidator, OnDestroy {
    subAccountId: string;
    masterAccountId: string;
    controlPreviousValue: string = '';
    controlValueAtFailure: string = '';
    millisecondsAtFailure: number;
    validatorPreviousResponse: ValidationErrors | null;
    destroy$ = new Subject<void>();
    isCreateTransactionPage: boolean;
    isCreateStandingAuthPage: boolean;
    wireIntermediary$: Observable<'yes' | 'no' | ''>;
    standingAuthWireIntermediary$: Observable<'yes' | 'no' | ''>;
    showIntermediaryFields$: Observable<boolean> | undefined;
    showIntermediaryFields: boolean = false;
    isIrsAba: boolean = false;
    private currentControl: AbstractControl | null = null;

    constructor(
        private store: Store,
        private wireService: WireService,
        private integrationService: IntegrationService,
        private validateWireFlagsService: ValidateWireFlagsService
    ) {
        this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(id => this.subAccountId = id);
        combineLatest([this.store.select(selectMasterAccount), this.store.select(selectPrimaryMaster)])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([masterAccount, primaryMaster]) => {
                this.masterAccountId = this.isPalAccountInContext() ? primaryMaster : masterAccount;
            });

        this.store.select(selectIsCreateTransaction).pipe(takeUntil(this.destroy$)).subscribe(val => this.isCreateTransactionPage = val);
        this.store.select(selectIsCreateStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(val => this.isCreateStandingAuthPage = val);
        combineLatest([this.store.select(selectWireInfo), this.store.select(selectStandingAuthWireInfo)])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([wireInfo, standingAuthWireInfo]) => {
                const isPreviousIrsAba = this.isIrsAba;
                this.isIrsAba = wireInfo.isIrsAba || standingAuthWireInfo.isIrsAba;
                if (isPreviousIrsAba !== this.isIrsAba) {
                    this.clearValidationCache();
                }
            });

        this.wireIntermediary$ = this.store.select(selectWireIntermediarySelection);
        this.standingAuthWireIntermediary$ = this.store.select(selectStandingAuthWireIntermediarySelection);
        this.showIntermediaryFields$ = combineLatest([this.wireIntermediary$, this.standingAuthWireIntermediary$]).pipe(
            map(([wireSelection, standingAuthSelection]) => wireSelection === 'yes' || standingAuthSelection === 'yes')
        );
        this.showIntermediaryFields$.pipe(takeUntil(this.destroy$)).subscribe(showIntermediary => {
            const previousShowIntermediaryFields = this.showIntermediaryFields;
            this.showIntermediaryFields = showIntermediary;
            if (previousShowIntermediaryFields !== this.showIntermediaryFields) {
                this.clearValidationCache();
            }
        });
    }

    validate(control: AbstractControl): Observable<ValidationErrors | null> {        
        this.currentControl = control;

        if (this.isIrsAba && control.value === this.controlPreviousValue && this.showIntermediaryFields) {
            const validatorResult = { 'isIrsAbaAndIntermediaryError': true };
            this.validatorPreviousResponse = validatorResult;
            if(!control?.touched){
                control.markAsTouched();
            }
            return of(validatorResult);
        }

        // Async validator is trigerring when changes to any subsequent controls are made
        // Previous response is saved and returned when same aba is provided to avoid repeated service call

        if (control.value === this.controlPreviousValue) {
            return of(this.validatorPreviousResponse);
        }

        // Allow same aba number if user retries after failed response (but prevent validator from retrying indefinitely)
        if (control.value === this.controlValueAtFailure) {
            let millisecondsSinceFailure = Date.now() - this.millisecondsAtFailure;
            if (millisecondsSinceFailure < 150) {
                return of(null);
            }
        }

        let payload: WireDetailsRequest = {
            subAccountId: this.subAccountId,
            masterAccountId: this.masterAccountId,
            abaNumber: control.value,
            isCreateSloa: this.isCreateStandingAuthPage,
            isOnetime: this.isCreateTransactionPage
        };
        control.disable();

        if (this.isCreateTransactionPage) {
            this.store.dispatch(WireActions.wireDetailsCallLoading());
        } else if (this.isCreateStandingAuthPage) {
            this.store.dispatch(StandingAuthWireActions.wireDetailsCallLoading());
        }

        const detailsRequest$ = this.wireService.callWireDetails(payload, '2', this.isPalAccountInContext());

        return detailsRequest$.pipe(
        
            map(resp => {
                if (this.isCreateTransactionPage) {
                    this.store.dispatch(WireActions.callWireDetails());
                } else if (this.isCreateStandingAuthPage) {
                    this.store.dispatch(StandingAuthWireActions.callWireDetails());
                }
                if (resp.abaStatusCode) {
                    const detailsResponse = this.mapDetailsResponseToWireInfo(resp);
                    if (this.isCreateTransactionPage) {
                        this.store.dispatch(WireActions.wireDetailsCallSuccessful(detailsResponse));
                    } else if (this.isCreateStandingAuthPage) {
                        this.store.dispatch(StandingAuthWireActions.wireDetailsCallSuccessful(detailsResponse));
                    }
                    if (resp.abaStatusCode === WireAbaCodes.ABA_ERROR) {
                        this.controlPreviousValue = control.value;
                        const validatorResult = { 'detailsApiError': true };
                        this.validatorPreviousResponse = validatorResult;
                        control.enable()
                        return validatorResult;
                    }
                    
                    if (resp.isIrsAba && this.showIntermediaryFields) {
                        this.controlPreviousValue = control.value;
                        const validatorResult = { 'isIrsAbaAndIntermediaryError': true };
                        this.validatorPreviousResponse = validatorResult;
                        control.enable()
                        return validatorResult;
                    }
                }
                this.controlPreviousValue = control.value;
                this.validatorPreviousResponse = null;
                control.enable()
                return null;
            }),
            catchError((err: ServiceError) => {
                this.controlValueAtFailure = control.value;
                this.millisecondsAtFailure = Date.now();
                if (this.isCreateTransactionPage) {
                    this.store.dispatch(WireActions.wireDetailsCallFailed(err));
                } else if (this.isCreateStandingAuthPage) {
                    this.store.dispatch(StandingAuthWireActions.wireDetailsCallFailed(err));
                }
                control.enable();
                return of(null);
            }),
        )
    }

    private isPalAccountInContext(): boolean {
        return isPalAccountFlow(
            this.integrationService.currentContext?.contextType as ContextType,
            this.validateWireFlagsService.enablePalWireCreateTransaction()
        );
    }

    private clearValidationCache(): void {
        this.validatorPreviousResponse = null;
        this.controlValueAtFailure = '';
        
        if (this.currentControl) {
            setTimeout(() => {
                this.currentControl?.updateValueAndValidity();
            }, 0);
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    mapDetailsResponseToWireInfo(wireDetailsResponse: WireDetailsApiResponse): DetailsResponse {
        return {
            abaStatusCode: wireDetailsResponse.abaStatusCode,
            receivingBankDetails: {
                recBankAba: wireDetailsResponse.recBankDetailIso.recBankAba,
                recBankName1: wireDetailsResponse.recBankDetail.recBankName1,
                recBankName2: wireDetailsResponse.recBankDetail.recBankName2,
                recBankAddress1: wireDetailsResponse.recBankDetail.recBankAddress1,
                recBankAddress2: wireDetailsResponse.recBankDetail.recBankAddress2,
                recBankCountry: wireDetailsResponse.recBankDetail.recBankCountry,
                bankPhoneArea: wireDetailsResponse.recBankDetail.bankPhoneArea,
                bankPhoneNum1: wireDetailsResponse.recBankDetail.bankPhoneNum1,
                bankPhoneNum2: wireDetailsResponse.recBankDetail.bankPhoneNum2,
                intermediaryAccount: wireDetailsResponse.recBankDetail.intermediaryAccount,
                intermediaryName1: wireDetailsResponse.recBankDetail.intermediaryName1,
                intermediaryName2: wireDetailsResponse.recBankDetail.intermediaryName2,
                intermediaryAddress1: wireDetailsResponse.recBankDetail.intermediaryAddress1,
                intermediaryAddress2: wireDetailsResponse.recBankDetail.intermediaryAddress2,
                interPhoneArea: wireDetailsResponse.recBankDetail.interPhoneArea,
                interPhoneNum1: wireDetailsResponse.recBankDetail.interPhoneNum1,
                interPhoneNum2: wireDetailsResponse.recBankDetail.interPhoneNum2,
                interPhoneExt: wireDetailsResponse.recBankDetail.interPhoneExt,
            },
            receivingBankDetailsIso: {
                recBankAba: wireDetailsResponse.recBankDetailIso.recBankAba,
                recBankName: wireDetailsResponse.recBankDetailIso.recBankName,
                recBankAddress: {
                    townName: wireDetailsResponse.recBankDetailIso.recBankAddress.townName,
                    department: "",
                    streetName: "",
                    buildingNumber: "",
                    buildingName: "",
                    floor: "",
                    room: "",
                    postCode: "",
                    townLocationName: "",
                    districtName: "",
                    countrySubDivision: wireDetailsResponse.recBankDetailIso.recBankAddress.countrySubDivision,
                    country: wireDetailsResponse.recBankDetailIso.recBankAddress.country,
                    concatenatedAddress: ""
                },
                intermediaryAccount: wireDetailsResponse.recBankDetailIso.intermediaryAccount,
                intermediaryName: wireDetailsResponse.recBankDetailIso.intermediaryName,
                intermediaryAddress: {
                    department: "",
                    streetName: "",
                    buildingNumber: "",
                    buildingName: "",
                    floor: "",
                    room: "",
                    postCode: "",
                    townName: wireDetailsResponse.recBankDetailIso.intermediaryAddress.townName,
                    townLocationName: "",
                    districtName: "",
                    countrySubDivision: wireDetailsResponse.recBankDetailIso.intermediaryAddress.countrySubDivision,
                    country: "",
                    concatenatedAddress: ""
                },
                recBankPhone: "",
                intermediaryPhone: ""
            },
            receivingBankAba: wireDetailsResponse.receivingBankAba,
            abaMessageFields: {
                errorList: wireDetailsResponse.messageFields.errorList,
                warningList: wireDetailsResponse.messageFields.warningList,
                atsSystemErrors: wireDetailsResponse.messageFields.atsSystemErrors
            },
            isIrsAba: wireDetailsResponse.isIrsAba
        }
    }
}
